import React from 'react';

import { Avatar, Skeleton } from '@mui/material';
import { OrganizationIcon } from 'src/components/icons/OrganizationIcon';
import { useCurrentUserQuery } from 'src/gql';
import type { UserWithOrgImg } from 'src/types';
import { makeStyles } from 'tss-react/mui';

interface StyleProps {
  height: number;
  width: number;
  backgroundColor?: string;
}

const useStyles = makeStyles<StyleProps>()((theme, { backgroundColor, height, width }) => ({
  avatarIcon: {
    height: height * 0.75,
    width: width * 0.75,
  },
  avatarImg: {
    backgroundColor: theme.palette.common.white,
    height,
    width,
  },
  defaultAvatar: {
    backgroundColor: backgroundColor ?? theme.palette.primary.main,
    height,
    width,
  },
}));

interface OrgAvatarProps {
  height?: number;
  width?: number;
}

export function CurrentUserOrgAvatar({ height = 40, width = 40 }: OrgAvatarProps): JSX.Element {
  const { currentUser, loading } = useCurrentUserQuery();
  return <OrgAvatar height={height} loading={loading} user={currentUser} width={width} />;
}

interface OrgAvatarPropsWithUser extends OrgAvatarProps {
  user: UserWithOrgImg | null;
  loading?: boolean;
}

export function OrgAvatar({
  height = 40,
  loading,
  user,
  width = 40,
}: OrgAvatarPropsWithUser): JSX.Element {
  const { classes } = useStyles({ backgroundColor: user?.org?.avatarColor, height, width });

  if (loading) return <Skeleton height={height} variant="circular" width={width} />;

  if (user?.org?.avatarImg)
    return <Avatar className={classes.avatarImg} src={user.org.avatarImg.filestackUrl} />;

  return (
    <Avatar className={classes.defaultAvatar}>
      <OrganizationIcon className={classes.avatarIcon} />
    </Avatar>
  );
}
